import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useLocale, Country } from "@/contexts/LocaleContext";
import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BidModal from "@/components/BidModal";
import { Button } from "@/components/ui/button";
import { Clock, Users, TrendingUp, ArrowLeft, Share2, Copy, Twitter } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { motion } from "framer-motion";
import { getTimeRemaining, isDeadlinePassed as checkDeadlinePassed, getEventStatusDisplay } from "@/lib/date-utils";

interface EventData {
  id: string;
  title: string;
  title_pt: string | null;
  description: string;
  description_pt: string | null;
  deadline: string;
  yes_pool: number;
  no_pool: number;
  total_bids: number;
  status: "OPEN" | "CLOSED" | "RESOLVED";
  language: string;
  outcome: boolean | null;
}

const languageFlags: Record<string, string> = {
  en: "🇺🇸",
  pt: "🇧🇷",
  es: "🇪🇸",
  fr: "🇫🇷",
  de: "🇩🇪",
  it: "🇮🇹",
  global: "🌍",
};

const Event = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, locale, formatCurrency } = useLocale();
  const { user } = useAuth();
  const { toast } = useToast();
  const [event, setEvent] = useState<EventData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bidModalOpen, setBidModalOpen] = useState(false);

  const fetchEvent = useCallback(async () => {
    if (!id) {
      setError("Event not found");
      setLoading(false);
      return;
    }

    const { data, error: fetchError } = await supabase
      .from("events")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (fetchError || !data) {
      setError(locale.language === "pt" ? "Evento não encontrado" : "Event not found");
      setLoading(false);
      return;
    }

    setEvent({
      ...data,
      language: data.country, // Map country field to language
    } as EventData);
    setLoading(false);
  }, [id, locale.language]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  // Use centralized utilities for time/status display

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <h1 className="text-4xl font-bold mb-4">404</h1>
          <p className="text-xl text-muted-foreground mb-4">{error || "Event not found"}</p>
          <Button onClick={() => navigate("/")} variant="hero">
            <ArrowLeft size={16} className="mr-2" />
            {locale.language === "pt" ? "Voltar ao início" : "Back to Home"}
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const title = locale.language === "pt" && event.title_pt ? event.title_pt : event.title;
  const description = locale.language === "pt" && event.description_pt ? event.description_pt : event.description;
  const totalPool = event.yes_pool + event.no_pool;
  const yesPercent = totalPool > 0 ? (event.yes_pool / totalPool) * 100 : 50;
  const noPercent = totalPool > 0 ? (event.no_pool / totalPool) * 100 : 50;
  const statusDisplay = getEventStatusDisplay(event.status, event.deadline, locale.language as "en" | "pt");
  const isDeadlinePassed = checkDeadlinePassed(event.deadline);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/")} 
          className="mb-6"
        >
          <ArrowLeft size={16} className="mr-2" />
          {locale.language === "pt" ? "Voltar" : "Back"}
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8"
        >
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <span className="text-2xl">{languageFlags[event.language] || "🌍"}</span>
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${statusDisplay.style}`}>
              {statusDisplay.text}
            </span>
            <div className="flex items-center gap-1 text-muted-foreground text-sm ml-auto">
              <Clock size={16} />
              {getTimeRemaining(event.deadline, locale.language as "en" | "pt")}
            </div>
          </div>

          {/* Title & Description */}
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            {title}
          </h1>
          <p className="text-muted-foreground mb-8">
            {description}
          </p>

          {/* Pool Distribution */}
          <div className="mb-8">
            <div className="flex justify-between text-lg mb-3">
              <span className="text-success font-bold">
                {locale.language === "pt" ? "SIM" : "YES"} {yesPercent.toFixed(0)}%
              </span>
              <span className="text-destructive font-bold">
                {locale.language === "pt" ? "NÃO" : "NO"} {noPercent.toFixed(0)}%
              </span>
            </div>
            <div className="h-4 bg-muted rounded-full overflow-hidden flex">
              <div
                className="h-full bg-gradient-to-r from-success to-success/70 transition-all duration-500"
                style={{ width: `${yesPercent}%` }}
              />
              <div
                className="h-full bg-gradient-to-r from-destructive/70 to-destructive transition-all duration-500"
                style={{ width: `${noPercent}%` }}
              />
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-between text-muted-foreground mb-8 p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <TrendingUp size={18} />
              <span className="font-semibold">{totalPool > 0 ? totalPool.toLocaleString() : "0"} {locale.language === "pt" ? "no pool" : "in pool"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users size={18} />
              <span className="font-semibold">{event.total_bids} {locale.language === "pt" ? "previsões" : "predictions"}</span>
            </div>
          </div>

          {/* Outcome Display */}
          {event.status === "RESOLVED" && event.outcome !== null && (
            <div className={`p-4 rounded-lg mb-8 text-center font-bold ${event.outcome ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"}`}>
              {locale.language === "pt" ? "Resultado: " : "Outcome: "}
              {event.outcome ? (locale.language === "pt" ? "SIM" : "YES") : (locale.language === "pt" ? "NÃO" : "NO")}
            </div>
          )}

          {/* Action Buttons */}
          {event.status === "OPEN" && !isDeadlinePassed ? (
            <div className="flex gap-4">
              <Button
                variant="success"
                size="lg"
                className="flex-1"
                onClick={() => {
                  if (!user) {
                    const returnTo = `/event/${event.id}`;
                    navigate(`/auth?returnTo=${encodeURIComponent(returnTo)}`, { state: { from: returnTo } });
                    return;
                  }
                  setBidModalOpen(true);
                }}
              >
                {locale.language === "pt" ? "SIM" : "YES"}
              </Button>
              <Button
                variant="danger"
                size="lg"
                className="flex-1"
                onClick={() => {
                  if (!user) {
                    const returnTo = `/event/${event.id}`;
                    navigate(`/auth?returnTo=${encodeURIComponent(returnTo)}`, { state: { from: returnTo } });
                    return;
                  }
                  setBidModalOpen(true);
                }}
              >
                {locale.language === "pt" ? "NÃO" : "NO"}
              </Button>
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              {event.status === "RESOLVED" 
                ? (locale.language === "pt" ? "Evento finalizado" : "Event finished")
                : (locale.language === "pt" ? "Previsões encerradas" : "Predictions closed")}
            </div>
          )}

          {/* Share Section */}
          <div className="flex justify-center mt-6">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                  <Share2 size={18} className="mr-2" />
                  {locale.language === "pt" ? "Compartilhar" : "Share"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2">
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      toast({
                        title: locale.language === "pt" ? "Link copiado!" : "Link copied!",
                        description: locale.language === "pt" ? "Compartilhe com seus amigos" : "Share it with your friends",
                      });
                    }}
                  >
                    <Copy size={16} className="mr-1" />
                    {locale.language === "pt" ? "Copiar" : "Copy"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const tweetText = encodeURIComponent(`${title} - ${locale.language === "pt" ? "Faça sua previsão no Callyout!" : "Make your prediction on Callyout!"}`);
                      const tweetUrl = encodeURIComponent(window.location.href);
                      window.open(`https://twitter.com/intent/tweet?text=${tweetText}&url=${tweetUrl}`, "_blank");
                    }}
                  >
                    <Twitter size={16} className="mr-1" />
                    Twitter
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const waText = encodeURIComponent(`${title} - ${window.location.href}`);
                      window.open(`https://wa.me/?text=${waText}`, "_blank");
                    }}
                  >
                    WhatsApp
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </motion.div>
      </main>
      <Footer />

      <BidModal
        isOpen={bidModalOpen}
        onClose={() => setBidModalOpen(false)}
        event={event ? {
          id: event.id,
          title: title,
          yes_pool: event.yes_pool,
          no_pool: event.no_pool,
          deadline: event.deadline,
        } : null}
        onBidPlaced={() => {
          // Refetch event data without page reload
          fetchEvent();
        }}
      />
    </div>
  );
};

export default Event;
